import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Trash2, GripVertical, ChevronUp, ChevronDown, Image as ImageIcon,
  Type, Layout, Users, Star, MessageSquare, Phone, Grid, ArrowRight,
  Edit, Eye, X, Upload, PawPrint
} from 'lucide-react';

// Available section types
const SECTION_TYPES = [
  { type: 'hero', label: 'Hero Banner', icon: Layout, description: 'Main banner with headline and call-to-action' },
  { type: 'about', label: 'About Us', icon: Users, description: 'Organization description and mission' },
  { type: 'features', label: 'Features/Services', icon: Grid, description: 'Highlight key features or services' },
  { type: 'gallery', label: 'Image Gallery', icon: ImageIcon, description: 'Showcase photos in a grid' },
  { type: 'testimonials', label: 'Testimonials', icon: MessageSquare, description: 'Customer reviews and stories' },
  { type: 'cta', label: 'Call to Action', icon: ArrowRight, description: 'Encourage visitors to take action' },
  { type: 'stats', label: 'Statistics', icon: Star, description: 'Show impressive numbers' },
  { type: 'team', label: 'Team Members', icon: Users, description: 'Introduce your team' },
  { type: 'contact', label: 'Contact Info', icon: Phone, description: 'Contact details and form' },
  { type: 'pets_featured', label: 'Featured Pets', icon: PawPrint, description: 'Showcase selected pets' },
  { type: 'custom', label: 'Custom Section', icon: Type, description: 'Free-form content section' },
];

// Default content for each section type
const getDefaultContent = (type, orgName = 'Our Organization') => {
  const defaults = {
    hero: {
      headline: `Welcome to ${orgName}`,
      subheadline: 'Making a difference, one pet at a time',
      buttonText: 'Learn More',
      buttonLink: '#about',
      backgroundImage: null,
      overlay: true
    },
    about: {
      title: 'About Us',
      content: `${orgName} is dedicated to providing the best care and finding loving homes for animals in need. Our mission is to rescue, rehabilitate, and rehome pets while educating the community about responsible pet ownership.`,
      image: null,
      imagePosition: 'right'
    },
    features: {
      title: 'What We Offer',
      subtitle: 'Our services and programs',
      items: [
        { title: 'Pet Adoption', description: 'Find your perfect companion', icon: 'heart' },
        { title: 'Foster Program', description: 'Temporary homes for pets', icon: 'home' },
        { title: 'Veterinary Care', description: 'Quality medical treatment', icon: 'medical' },
        { title: 'Community Events', description: 'Join our activities', icon: 'calendar' }
      ]
    },
    gallery: {
      title: 'Our Gallery',
      subtitle: 'Moments that matter',
      images: [],
      columns: 3
    },
    testimonials: {
      title: 'What People Say',
      subtitle: 'Stories from our adopters',
      items: [
        { name: 'Happy Adopter', quote: 'Best decision we ever made!', image: null },
        { name: 'Volunteer', quote: 'Amazing organization to work with.', image: null }
      ]
    },
    cta: {
      title: 'Ready to Make a Difference?',
      subtitle: 'Join our mission to help animals in need',
      buttonText: 'Get Involved',
      buttonLink: '/contact',
      backgroundColor: 'primary'
    },
    stats: {
      title: 'Our Impact',
      items: [
        { number: '500+', label: 'Pets Adopted' },
        { number: '100+', label: 'Volunteers' },
        { number: '10+', label: 'Years of Service' },
        { number: '1000+', label: 'Lives Changed' }
      ]
    },
    team: {
      title: 'Meet Our Team',
      subtitle: 'Dedicated to animal welfare',
      members: [
        { name: 'Team Member', role: 'Director', image: null, bio: '' }
      ]
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'We\'d love to hear from you',
      showForm: true,
      showMap: false,
      showInfo: true
    },
    pets_featured: {
      title: 'Featured Pets',
      subtitle: 'Looking for their forever homes',
      maxPets: 4,
      showAdoptButton: true
    },
    custom: {
      title: 'Custom Section',
      content: 'Add your custom content here.',
      backgroundColor: 'white'
    }
  };
  return defaults[type] || defaults.custom;
};

// Section Editor Component
const SectionEditor = ({ section, onUpdate, onClose }) => {
  const [editedContent, setEditedContent] = useState(section.content || {});
  
  const handleImageUpload = useCallback(async (acceptedFiles, field) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', 'section');

    try {
      const response = await axios.post('/website/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setEditedContent(prev => ({ ...prev, [field]: response.data.url }));
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (files) => handleImageUpload(files, 'image'),
    accept: { 'image/*': [] },
    maxFiles: 1
  });

  const handleSave = () => {
    onUpdate({ ...section, content: editedContent });
    onClose();
  };

  const renderEditor = () => {
    switch (section.type) {
      case 'hero':
        return (
          <div className="space-y-4">
            <div>
              <Label>Headline</Label>
              <Input 
                value={editedContent.headline || ''} 
                onChange={(e) => setEditedContent({...editedContent, headline: e.target.value})}
              />
            </div>
            <div>
              <Label>Subheadline</Label>
              <Input 
                value={editedContent.subheadline || ''} 
                onChange={(e) => setEditedContent({...editedContent, subheadline: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Button Text</Label>
                <Input 
                  value={editedContent.buttonText || ''} 
                  onChange={(e) => setEditedContent({...editedContent, buttonText: e.target.value})}
                />
              </div>
              <div>
                <Label>Button Link</Label>
                <Input 
                  value={editedContent.buttonLink || ''} 
                  onChange={(e) => setEditedContent({...editedContent, buttonLink: e.target.value})}
                />
              </div>
            </div>
            <div>
              <Label>Background Image</Label>
              <div {...getRootProps()} className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary">
                <input {...getInputProps()} />
                {editedContent.backgroundImage ? (
                  <div className="relative">
                    <img src={editedContent.backgroundImage} alt="Hero" className="h-32 w-full object-cover rounded" />
                    <Button 
                      size="sm" variant="destructive" 
                      className="absolute top-2 right-2"
                      onClick={(e) => { e.stopPropagation(); setEditedContent({...editedContent, backgroundImage: null}); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-stone-500 py-4">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click or drag to upload</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'about':
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Title</Label>
              <Input 
                value={editedContent.title || ''} 
                onChange={(e) => setEditedContent({...editedContent, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea 
                value={editedContent.content || ''} 
                onChange={(e) => setEditedContent({...editedContent, content: e.target.value})}
                rows={5}
              />
            </div>
            <div>
              <Label>Image</Label>
              <div {...getRootProps()} className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary">
                <input {...getInputProps()} />
                {editedContent.image ? (
                  <div className="relative">
                    <img src={editedContent.image} alt="About" className="h-32 mx-auto object-cover rounded" />
                    <Button 
                      size="sm" variant="destructive" 
                      className="absolute top-2 right-2"
                      onClick={(e) => { e.stopPropagation(); setEditedContent({...editedContent, image: null}); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="text-stone-500 py-4">
                    <Upload className="w-8 h-8 mx-auto mb-2" />
                    <p className="text-sm">Click or drag to upload</p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <Label>Image Position</Label>
              <Select value={editedContent.imagePosition || 'right'} onValueChange={(v) => setEditedContent({...editedContent, imagePosition: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'features':
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Title</Label>
              <Input 
                value={editedContent.title || ''} 
                onChange={(e) => setEditedContent({...editedContent, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input 
                value={editedContent.subtitle || ''} 
                onChange={(e) => setEditedContent({...editedContent, subtitle: e.target.value})}
              />
            </div>
            <div>
              <Label>Features (one per line: Title | Description)</Label>
              <Textarea 
                value={(editedContent.items || []).map(i => `${i.title} | ${i.description}`).join('\n')} 
                onChange={(e) => {
                  const items = e.target.value.split('\n').map(line => {
                    const [title, description] = line.split('|').map(s => s.trim());
                    return { title: title || '', description: description || '', icon: 'star' };
                  });
                  setEditedContent({...editedContent, items});
                }}
                rows={6}
                placeholder="Feature Title | Feature description"
              />
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Title</Label>
              <Input 
                value={editedContent.title || ''} 
                onChange={(e) => setEditedContent({...editedContent, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Columns</Label>
              <Select value={String(editedContent.columns || 3)} onValueChange={(v) => setEditedContent({...editedContent, columns: parseInt(v)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Columns</SelectItem>
                  <SelectItem value="3">3 Columns</SelectItem>
                  <SelectItem value="4">4 Columns</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Gallery Images</Label>
              <GalleryUploader 
                images={editedContent.images || []} 
                onUpdate={(images) => setEditedContent({...editedContent, images})}
              />
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input 
                value={editedContent.title || ''} 
                onChange={(e) => setEditedContent({...editedContent, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Subtitle</Label>
              <Input 
                value={editedContent.subtitle || ''} 
                onChange={(e) => setEditedContent({...editedContent, subtitle: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Button Text</Label>
                <Input 
                  value={editedContent.buttonText || ''} 
                  onChange={(e) => setEditedContent({...editedContent, buttonText: e.target.value})}
                />
              </div>
              <div>
                <Label>Button Link</Label>
                <Input 
                  value={editedContent.buttonLink || ''} 
                  onChange={(e) => setEditedContent({...editedContent, buttonLink: e.target.value})}
                />
              </div>
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Title</Label>
              <Input 
                value={editedContent.title || ''} 
                onChange={(e) => setEditedContent({...editedContent, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Statistics (one per line: Number | Label)</Label>
              <Textarea 
                value={(editedContent.items || []).map(i => `${i.number} | ${i.label}`).join('\n')} 
                onChange={(e) => {
                  const items = e.target.value.split('\n').map(line => {
                    const [number, label] = line.split('|').map(s => s.trim());
                    return { number: number || '0', label: label || '' };
                  });
                  setEditedContent({...editedContent, items});
                }}
                rows={4}
                placeholder="100+ | Happy Customers"
              />
            </div>
          </div>
        );

      case 'custom':
      default:
        return (
          <div className="space-y-4">
            <div>
              <Label>Section Title</Label>
              <Input 
                value={editedContent.title || ''} 
                onChange={(e) => setEditedContent({...editedContent, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea 
                value={editedContent.content || ''} 
                onChange={(e) => setEditedContent({...editedContent, content: e.target.value})}
                rows={6}
              />
            </div>
          </div>
        );
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Edit className="w-5 h-5" />
          Edit {SECTION_TYPES.find(s => s.type === section.type)?.label || 'Section'}
        </DialogTitle>
      </DialogHeader>
      {renderEditor()}
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave}>Save Changes</Button>
      </DialogFooter>
    </DialogContent>
  );
};

// Gallery Uploader Component
const GalleryUploader = ({ images, onUpdate }) => {
  const handleUpload = useCallback(async (acceptedFiles) => {
    for (const file of acceptedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('image_type', 'gallery');

      try {
        const response = await axios.post('/website/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        onUpdate([...images, { url: response.data.url, caption: '' }]);
      } catch (error) {
        toast.error('Failed to upload image');
      }
    }
  }, [images, onUpdate]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleUpload,
    accept: { 'image/*': [] },
    multiple: true
  });

  const removeImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onUpdate(newImages);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2">
        {images.map((img, idx) => (
          <div key={idx} className="relative group">
            <img src={img.url} alt={`Gallery ${idx}`} className="w-full h-20 object-cover rounded" />
            <Button 
              size="sm" variant="destructive" 
              className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => removeImage(idx)}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
      <div {...getRootProps()} className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-primary">
        <input {...getInputProps()} />
        <Upload className="w-6 h-6 mx-auto mb-1 text-stone-400" />
        <p className="text-sm text-stone-500">Drop images here or click to upload</p>
      </div>
    </div>
  );
};

// Main Section Builder Component
const SectionBuilder = ({ sections = [], onUpdate, organizationName }) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [selectedType, setSelectedType] = useState(null);

  const addSection = (type) => {
    const newSection = {
      id: `section_${uuidv4()}`,
      type,
      order: sections.length,
      visible: true,
      content: getDefaultContent(type, organizationName)
    };
    onUpdate([...sections, newSection]);
    setShowAddDialog(false);
    setSelectedType(null);
    toast.success('Section added! Click Edit to customize.');
  };

  const removeSection = (sectionId) => {
    if (window.confirm('Delete this section?')) {
      onUpdate(sections.filter(s => s.id !== sectionId));
      toast.success('Section removed');
    }
  };

  const moveSection = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    const newSections = [...sections];
    const temp = newSections[index];
    newSections[index] = newSections[newIndex];
    newSections[newIndex] = temp;
    
    // Update order numbers
    newSections.forEach((s, i) => { s.order = i; });
    onUpdate(newSections);
  };

  const toggleVisibility = (sectionId) => {
    onUpdate(sections.map(s => 
      s.id === sectionId ? { ...s, visible: !s.visible } : s
    ));
  };

  const updateSection = (updatedSection) => {
    onUpdate(sections.map(s => 
      s.id === updatedSection.id ? updatedSection : s
    ));
    setEditingSection(null);
  };

  const getSectionIcon = (type) => {
    const found = SECTION_TYPES.find(s => s.type === type);
    return found ? found.icon : Type;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold">Website Sections</h3>
          <p className="text-sm text-stone-500">Add, edit, and reorder sections on your homepage</p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" /> Add Section
        </Button>
      </div>

      {/* Sections List */}
      <div className="space-y-2">
        {sections.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-8 text-center text-stone-500">
              <Layout className="w-12 h-12 mx-auto mb-3 text-stone-300" />
              <p>No sections added yet</p>
              <p className="text-sm">Click &quot;Add Section&quot; to start building your homepage</p>
            </CardContent>
          </Card>
        ) : (
          sections.sort((a, b) => a.order - b.order).map((section, index) => {
            const Icon = getSectionIcon(section.type);
            const typeInfo = SECTION_TYPES.find(t => t.type === section.type);
            
            return (
              <Card key={section.id} className={`${!section.visible ? 'opacity-50' : ''}`}>
                <CardContent className="py-3 flex items-center gap-3">
                  {/* Drag Handle (visual only for now) */}
                  <div className="text-stone-400">
                    <GripVertical className="w-5 h-5" />
                  </div>
                  
                  {/* Move Buttons */}
                  <div className="flex flex-col">
                    <Button
                      size="sm" variant="ghost" className="h-5 w-5 p-0"
                      onClick={() => moveSection(index, 'up')}
                      disabled={index === 0}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="h-5 w-5 p-0"
                      onClick={() => moveSection(index, 'down')}
                      disabled={index === sections.length - 1}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Section Icon & Info */}
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{typeInfo?.label || section.type}</p>
                      <p className="text-xs text-stone-500">
                        {section.content?.title || section.content?.headline || 'Click edit to customize'}
                      </p>
                    </div>
                  </div>

                  {/* Visibility Badge */}
                  <Badge 
                    variant={section.visible ? 'default' : 'outline'} 
                    className="cursor-pointer"
                    onClick={() => toggleVisibility(section.id)}
                  >
                    {section.visible ? 'Visible' : 'Hidden'}
                  </Badge>

                  {/* Actions */}
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEditingSection(section)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeSection(section.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Add Section Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Section</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto py-2">
            {SECTION_TYPES.map((sectionType) => {
              const Icon = sectionType.icon;
              return (
                <Card 
                  key={sectionType.type}
                  className={`cursor-pointer transition-all hover:border-primary ${selectedType === sectionType.type ? 'border-primary ring-2 ring-primary/20' : ''}`}
                  onClick={() => setSelectedType(sectionType.type)}
                >
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{sectionType.label}</p>
                      <p className="text-xs text-stone-500">{sectionType.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={() => addSection(selectedType)} disabled={!selectedType}>
              Add Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Section Dialog */}
      <Dialog open={!!editingSection} onOpenChange={() => setEditingSection(null)}>
        {editingSection && (
          <SectionEditor 
            section={editingSection} 
            onUpdate={updateSection}
            onClose={() => setEditingSection(null)}
          />
        )}
      </Dialog>
    </div>
  );
};

export default SectionBuilder;
